import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse} from '@angular/common/http';
import { BehaviorSubject, catchError, concatMap, EMPTY, filter, finalize, Observable, retry, take, throwError, timer } from 'rxjs';
import { AuthenticationService } from '../../services/AuthenticationService';
import { AppConfig } from '../../app.config';
import { Router } from '@angular/router';
import { Tokens } from '../../models/tokens';
 
export const retryCount = 4;
export const retryBaseDelayMs = 500;
export const retryMaxDelayMs = 8000;
 
// Three explicit states instead of an overloaded boolean:
// - Pending: a refresh call is currently in flight
// - Success: the in-flight refresh completed successfully
// - Failed:  the in-flight refresh completed with an error
enum TokenRefreshStatus {
    Pending = 'PENDING',
    Success = 'SUCCESS',
    Failed = 'FAILED'
}
 
@Injectable()
export class AppInterceptor implements HttpInterceptor {
 
    isRefreshingToken = false;
    tokenRefreshedSubject = new BehaviorSubject<TokenRefreshStatus>(TokenRefreshStatus.Success);
    // Tracks whether the most recent failure was an invalid refresh token
    // (unrecoverable, user is being logged out) vs a transient error.
    lastRefreshWasInvalidToken = false;
 
    constructor(private authService:AuthenticationService, private appConfig:AppConfig, private router:Router) {}
 
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.url.endsWith("/login") || request.url.endsWith("/refresh")) {
            return next.handle(request);
        }
        else{
            return next.handle(request).pipe(
                catchError((error: unknown) => {
                    if (error instanceof HttpErrorResponse && this.checkTokenExpiryErr(error)) {
                        return this.handle401Error(request, next);
                    }
                    return throwError(()=>(error));
                })
            );
        }
    }
 
    private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (this.isRefreshingToken) {
            // Wait for the in-flight refresh to settle, one way or the other.
            // Filtering out "Pending" means we never resolve on the transient
            // in-progress state, and we never hang forever either, since
            // Success/Failed are always eventually emitted in the finalize below.
            return this.tokenRefreshedSubject.pipe(
                filter(status => status !== TokenRefreshStatus.Pending),
                take(1),
                concatMap(status => {
                    if (status === TokenRefreshStatus.Success) {
                        return next.handle(this.updateHeader(req));
                    }
                    // Refresh failed while we were queued behind it.
                    // If it was an invalid token, we're logging out - don't
                    // surface an error, just stop this request silently.
                    if (this.lastRefreshWasInvalidToken) {
                        return EMPTY;
                    }
                    return throwError(() => new Error('Token refresh failed'));
                })
            );
        }
 
        this.isRefreshingToken = true;
        this.tokenRefreshedSubject.next(TokenRefreshStatus.Pending);
 
        return this.authService.refreshTokens().pipe(
            retry({
                count: retryCount,
                delay: (error, retryIndex) => {
                    // Don't retry a genuinely invalid refresh token - that will
                    // never succeed no matter how many times we ask.
                    if (error instanceof HttpErrorResponse && !this.isRetryableError(error)) {
                        throw error;
                    }
                    const backoff = Math.min(retryBaseDelayMs * Math.pow(2, retryIndex - 1), retryMaxDelayMs);
                    const jitter = Math.random() * 0.3 * backoff; // ±30% jitter
                    return timer(backoff + jitter);
                }
            }),
            concatMap((res: Tokens) => {
                this.tokenRefreshedSubject.next(TokenRefreshStatus.Success);
                return next.handle(this.updateHeader(req));
            }),
            catchError((error) => {
                // Unblock any requests queued behind this refresh, regardless
                // of outcome, so they fail fast instead of hanging.
                const isInvalid = error instanceof HttpErrorResponse && this.checkInvalidAttemptErr(error);
                this.lastRefreshWasInvalidToken = isInvalid;
                this.tokenRefreshedSubject.next(TokenRefreshStatus.Failed);
 
                // Only tear down the session on a genuinely invalid refresh
                // token. A transient failure (network blip, 5xx, timeout)
                // should not log the user out from under them.
                if (isInvalid) {
                    this.authService.logout();
                    // Session is dead and we're navigating to /login - don't
                    // let the original request's subscriber run error logic
                    // (toasts, retries, etc.) on the way out.
                    return EMPTY;
                } else {
                    this.router.navigate(['/']);
                    // Transient failure - this is a real error the caller
                    // may legitimately want to know about.
                    return throwError(() => error);
                }
            }),
            finalize(() => {
                this.isRefreshingToken = false;
            })
        );
    }

    private isRetryableError(error: HttpErrorResponse): boolean {
        return error.status === 0 ||               // network failure / no response
            error.status === 500 ||
            error.status === 502 ||
            error.status === 503 ||
            error.status === 504;
    }
 
    private checkTokenExpiryErr(error: HttpErrorResponse): boolean {
        return error.status === 401 && error.headers.has('is-token-expired');
    }
 
    private checkInvalidAttemptErr(error: HttpErrorResponse): boolean {
        return (
            error.status === 401 &&
            typeof error.error === 'string' &&
            error.error.startsWith('Invalid attempt!')
        );
    }
 
    updateHeader(request:any) {
        if (this.appConfig.authHeader) {
            return request.clone({
                setHeaders: {
                    "Authorization": this.appConfig.authHeader
                }
            });
        }
        return request;
    }
 
    logout() {
        this.router.navigate(['/login']);
        this.authService.logout();
    }
}